import { TRAIL_FADE_ALPHA } from '../constants/feel';

export const TRAIL_PIECE_RADIUS = 18;

export const TRAIL_TOUCH_MARGIN = 24;

export const TRAIL_JUMP_LIMIT = 250;

export function hasTrail(trailProductId: number): boolean {
  return trailProductId > 0;
}

interface ColourRibbon {
  readonly kind: 'colour';
  readonly color: string;
}

interface SpriteRibbon {
  readonly kind: 'sprite';
  readonly file: string;
  readonly width: number;
  readonly height: number;
  readonly every: number;
  readonly setback: number;
  readonly orientation: 'heading' | 'upright' | 'random';
  readonly scatter: number;
  readonly under: boolean;
}

interface GlyphRibbon {
  readonly kind: 'glyph';
  readonly glyphs: string;
  readonly size: number;
  readonly every: number;
  readonly setback: number;
  readonly spread: number;
  readonly headColor: string;
  readonly bodyColor: string;
  readonly headChance: number;
}

export type Ribbon = ColourRibbon | SpriteRibbon | GlyphRibbon;

export const GLYPH_FONT_WEIGHT = 'bold';

export const GLYPH_FONT_FAMILY =
  "'Noto Sans CJK JP', 'Hiragino Kaku Gothic ProN', 'Yu Gothic', 'MS Gothic', monospace";

export const MATRIX_GLYPHS =
  'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワン0123456789';

export const RIBBONS: Readonly<Record<number, Ribbon>> = {
  8: {
    kind: 'sprite',
    file: 'rainbow',
    width: 50,
    height: 20,
    every: 1,
    setback: 7,
    orientation: 'heading',
    scatter: 0,
    under: true,
  },
  9: {
    kind: 'sprite',
    file: 'doge',
    width: 50,
    height: 50,
    every: 10,
    setback: 10,
    orientation: 'upright',
    scatter: 0,
    under: true,
  },
  10: {
    kind: 'sprite',
    file: 'heart',
    width: 20,
    height: 20,
    every: 3,
    setback: 0,
    orientation: 'random',
    scatter: 20,
    under: false,
  },
  50: {
    kind: 'sprite',
    file: 'rob',
    width: 75,
    height: 75,
    every: 11,
    setback: 15,
    orientation: 'random',
    scatter: 0,
    under: false,
  },
  51: { kind: 'colour', color: 'rgb(255, 255, 255)' },
  52: { kind: 'colour', color: 'rgb(255, 0, 0)' },
  53: { kind: 'colour', color: 'rgb(0, 0, 255)' },
  54: { kind: 'colour', color: 'rgb(0, 255, 0)' },
  105: { kind: 'colour', color: 'rgb(249, 164, 164)' },
  125: {
    kind: 'glyph',
    glyphs: MATRIX_GLYPHS,
    size: 26,
    every: 4,
    setback: 35,
    spread: 40,
    headColor: 'rgb(205, 255, 215)',
    bodyColor: 'rgb(0, 224, 60)',
    headChance: 0.22,
  },
};

export function ribbonReach(ribbon: Ribbon): number {
  switch (ribbon.kind) {
    case 'colour':
      return TRAIL_PIECE_RADIUS;
    case 'sprite':
      return ribbon.width;
    case 'glyph':
      return ribbon.size + ribbon.setback + ribbon.spread;
  }
}

export function ribbonFor(trailProductId: number): Ribbon | null {
  return RIBBONS[trailProductId] ?? null;
}

export function trailSurvival(frames: number): number {
  return TRAIL_FADE_ALPHA ** frames;
}

export interface TrailAnchor {
  x: number;
  y: number;
  angle: number;
  frame: number;
}

export const TRAIL_ANGLE_STEP = 0.1;

export function continuesTrail(
  anchor: TrailAnchor | undefined,
  x: number,
  y: number,
  frame: number,
): boolean {
  if (anchor === undefined || anchor.frame !== frame - 1) return false;
  return Math.hypot(x - anchor.x, y - anchor.y) <= TRAIL_JUMP_LIMIT;
}

/** By value: a caller that then moves the anchor must not collapse its own segment to a point. */
export function trailSegment(
  anchor: TrailAnchor | undefined,
  x: number,
  y: number,
  angle: number,
  frame: number,
): { fromX: number; fromY: number; fromAngle: number } {
  if (anchor === undefined || !continuesTrail(anchor, x, y, frame)) {
    return { fromX: x, fromY: y, fromAngle: angle };
  }
  return { fromX: anchor.x, fromY: anchor.y, fromAngle: anchor.angle };
}

export function angleDelta(from: number, to: number): number {
  const turn = Math.PI * 2;
  return ((((to - from) % turn) + turn * 1.5) % turn) - Math.PI;
}

export const MAX_TRAIL_INFILL = 24;

export function trailInfillCount(
  distance: number,
  turn: number,
  spriteWidth: number,
  scale: number,
): number {
  const spacing = Math.max(1, (spriteWidth * scale) / 2);
  const steps = Math.max(distance / spacing, Math.abs(turn) / TRAIL_ANGLE_STEP);
  return Math.min(MAX_TRAIL_INFILL, Math.max(0, Math.floor(steps)));
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const TRAIL_FADE_OUT_FRAMES = Math.ceil(Math.log(1 / 255) / Math.log(TRAIL_FADE_ALPHA));

function empty(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

function isEmpty(box: Bounds): boolean {
  return box.minX > box.maxX;
}

/** Two canvases, ping-ponged, because a canvas cannot be blitted onto itself at an offset. */
export class TrailBuffer {
  private front: HTMLCanvasElement | null = null;
  private back: HTMLCanvasElement | null = null;
  private host: HTMLElement | null = null;
  private attached = false;
  private lastCameraX = Number.NaN;
  private lastCameraY = Number.NaN;

  /** One box per frame of the fade window: past it a pixel is below one 8-bit level, so dropping it cannot change the picture. */
  private readonly history: Bounds[] = [];

  private frame = 0;

  get frameCount(): number {
    return this.frame;
  }

  get canvas(): HTMLCanvasElement | null {
    return this.front;
  }

  /** Hands the two buffers to the compositor instead of blitting them per frame; the pair swaps every frame, so only the front one is ever visible. */
  mount(host: HTMLElement): void {
    this.host = host;
    this.attached = false;
  }

  advance(
    deviceWidth: number,
    deviceHeight: number,
    cameraX: number,
    cameraY: number,
    worldScale: number,
  ): CanvasRenderingContext2D | null {
    if (deviceWidth <= 0 || deviceHeight <= 0) return null;
    const width = Math.max(1, Math.round(deviceWidth));
    const height = Math.max(1, Math.round(deviceHeight));
    if (this.front === null || this.front.width !== width || this.front.height !== height) {
      this.front?.remove();
      this.back?.remove();
      this.front = createCanvas(width, height);
      this.back = createCanvas(width, height);
      if (this.front === null || this.back === null) return null;
      this.lastCameraX = cameraX;
      this.lastCameraY = cameraY;
      this.attached = false;
      this.history.length = 0;
    }
    const source = this.front;
    const dest = this.back;
    if (dest === null) return null;

    const host = this.host;
    if (host !== null && !this.attached) {
      this.attached = true;
      for (const buffer of [source, dest]) {
        buffer.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%';
        host.append(buffer);
      }
    }

    const context = dest.getContext('2d');
    if (context === null) return null;

    const dragX = Number.isNaN(this.lastCameraX) ? 0 : this.lastCameraX - cameraX;
    const dragY = Number.isNaN(this.lastCameraY) ? 0 : this.lastCameraY - cameraY;
    this.lastCameraX = cameraX;
    this.lastCameraY = cameraY;

    const shiftX = dragX * worldScale;
    const shiftY = dragY * worldScale;
    for (const box of this.history) {
      if (isEmpty(box)) continue;
      box.minX += shiftX;
      box.maxX += shiftX;
      box.minY += shiftY;
      box.maxY += shiftY;
    }
    while (this.history.length >= TRAIL_FADE_OUT_FRAMES) this.history.shift();
    this.history.push(empty());

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, width, height);
    context.globalAlpha = TRAIL_FADE_ALPHA;
    context.globalCompositeOperation = 'source-over';
    context.drawImage(source, shiftX, shiftY);
    context.globalAlpha = 1;

    this.front = dest;
    this.back = source;
    if (this.host !== null) {
      dest.style.visibility = 'visible';
      source.style.visibility = 'hidden';
    }
    this.frame++;
    return context;
  }

  touch(minX: number, minY: number, maxX: number, maxY: number): void {
    const box = this.history[this.history.length - 1];
    if (box === undefined) return;
    box.minX = Math.min(box.minX, minX);
    box.minY = Math.min(box.minY, minY);
    box.maxX = Math.max(box.maxX, maxX);
    box.maxY = Math.max(box.maxY, maxY);
  }

  /** Device pixels; null when nothing is in it. */
  get dirty(): Bounds | null {
    const union = empty();
    for (const box of this.history) {
      if (isEmpty(box)) continue;
      union.minX = Math.min(union.minX, box.minX);
      union.minY = Math.min(union.minY, box.minY);
      union.maxX = Math.max(union.maxX, box.maxX);
      union.maxY = Math.max(union.maxY, box.maxY);
    }
    return isEmpty(union) ? null : union;
  }

  reset(): void {
    this.front?.remove();
    this.back?.remove();
    this.front = null;
    this.back = null;
    this.attached = false;
    this.lastCameraX = Number.NaN;
    this.lastCameraY = Number.NaN;
    this.history.length = 0;
    /** +2, not +1: `continuesTrail` only needs `anchor.frame === frame - 1`, so a single bump still reads a same-tick stale anchor as consecutive. */
    this.frame += 2;
  }
}

function createCanvas(width: number, height: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
